
import { createAtom } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { PromiseBuilder__Delay_62FBFDE1, PromiseBuilder__Run_212F1D4B } from "../fable_modules/Fable.Promise.3.1.3/Promise.fs.js";
import { promise } from "../fable_modules/Fable.Promise.3.1.3/PromiseImpl.fs.js";
import { maskOfNames, setCategories, dbg } from "../Shared/Log.js";
import { ofArray, singleton, map, head, tail, isEmpty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { toText, printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";
import { tryParse } from "../fable_modules/fable-library-js.5.13.0/Long.js";
import { FSharpRef } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { fromZero, tryParse as tryParse_1 } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { logSwitch, hasWSwitch, hasDebugSwitch } from "./Bridge.js";
import { v4 } from "uuid";

export let memSize = createAtom(0);

export function getProcessPrivateMemory() {
    const memInfo = window.issieBridge.diagnostics.processMemory();
    const pr = PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => (memInfo)));
    void (pr.then((info) => {
        memSize(~~(info.private / 1024));
        dbg(512, `memory: private=${memSize()}, resident=${info.resident}`);
    }));
    return memSize() | 0;
}

/**
 * Assert js object is not null, and return it.
 */
export function assertNotNull(obj, msg) {
    return obj;
}

/**
 * Access nested fields of a js object, failing if at any point of the chain
 * the requested field is null.
 * Should be used when the fields are guaranteed to exist.
 * For example ["a"; "b"; "c"] is equivalent to the jsCode `obj.a.b.c`, but
 * with checks against null at every layer.
 */
export function getFailIfNull(jsObj_mut, fields_mut) {
    getFailIfNull:
    while (true) {
        const jsObj = jsObj_mut, fields = fields_mut;
        let msg_1;
        assertNotNull(jsObj, "jsObj is null in getFailIfNull");
        if (isEmpty(fields)) {
            return toFail(printf("what? getFailIfNull called with no fields to get"));
        }
        else if (isEmpty(tail(fields))) {
            const msg = toText(printf("jsObj.%s is null in getFailIfNull"))(head(fields));
            return assertNotNull(jsObj[head(fields)], msg);
        }
        else {
            jsObj_mut = ((msg_1 = toText(printf("jsObj.%s is null in getFailIfNull"))(head(fields)), assertNotNull(jsObj[head(fields)], msg_1)));
            fields_mut = tail(fields);
            continue getFailIfNull;
        }
        break;
    }
}

/**
 * Transforms a js list of jsType into an f# list of jsType.
 * If jsList is not a js list, fail.
 */
export function jsListToFSharpList(jsList) {
    return map((i) => jsList[i], toList(rangeDouble(0, 1, getFailIfNull(jsList, singleton("length")) - 1)));
}

export function fshaprListToJsList(list) {
    const jsList = [];
    map((el) => (jsList.push(el)), list);
    return jsList;
}

/**
 * Get the value for a change event in an input textbox.
 */
export function getTextEventValue(event) {
    return getFailIfNull(event.currentTarget, singleton("value"));
}

/**
 * Get the value for a change event in an input number box,
 * making sure it is an F# integer (JS integer values may not be precise)
 */
export function getIntEventValue(event) {
    return ~~getFailIfNull(event.currentTarget, singleton("value")) | 0;
}

export function getFloatEventValue(event) {
    return getFailIfNull(event.currentTarget, singleton("value"));
}

export function getInt64EventValue(event) {
    let boxT1;
    const boxText = getFailIfNull(event, ofArray(["target", "value"]));
    let patternInput;
    let outArg = 0n;
    patternInput = [tryParse((boxT1 = boxText.toLocaleLowerCase(), (((boxText.length > 1) && (boxT1[0] === "x")) ? true : (boxT1[0] === "b")) ? ("0" + boxText) : boxText), 511, false, 64, new FSharpRef(() => outArg, (v) => {
        outArg = v;
    })), outArg];
    if (!patternInput[0]) {
        return 0n;
    }
    else {
        return patternInput[1];
    }
}

export function getBigintEventValue(event) {
    let boxT1;
    const boxText = getFailIfNull(event, ofArray(["target", "value"]));
    let patternInput;
    let outArg = 0n;
    patternInput = [tryParse_1((boxT1 = boxText.toLocaleLowerCase(), (((boxText.length > 1) && (boxT1[0] === "x")) ? true : (boxT1[0] === "b")) ? ("0" + boxText) : boxText), new FSharpRef(() => outArg, (v) => {
        outArg = v;
    })), outArg];
    if (!patternInput[0]) {
        return fromZero();
    }
    else {
        return patternInput[1];
    }
}

/**
 * Get the value for a blur event in an input textbox.
 */
export function getTextFocusEventValue(event) {
    return getFailIfNull(event, ofArray(["target", "value"]));
}

export let debugLevel = createAtom(0);

export let loggingMemory = createAtom(false);

export const productionBuild = false;

/**
 * Hack to provide a constant global variable
 * set from command line arguments of main process.
 * 0 => production. 1 => dev. 2 => debug.
 * 
 * Also reads --log=wire,sim, which is the only way to have a log category on before the first
 * line of startup runs - the Development menu and window.issieLog can only be reached later,
 * by which time loading a project is already over.
 */
export function setDebugLevel() {
    if (hasDebugSwitch) {
        debugLevel(2);
    }
    else if (hasWSwitch) {
        debugLevel(1);
    }
    if (logSwitch !== "") {
        setCategories(maskOfNames(logSwitch));
    }
}

export const uuid = v4;

export function delayedDispatch(dispatch, delayInMs, msg) {
    return setTimeout(() => {
        dispatch(msg);
    }, delayInMs) | 0;
}

export function periodicDispatch(dispatch, delayInMs, msg) {
    return setInterval(() => {
        dispatch(msg);
    }, delayInMs) | 0;
}

